
import { createClient } from '@supabase/supabase-js';
import type { Database } from '@/types/supabase';
import { yearFromLockError } from '@/utils/year-lock';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

/**
 * Signal a closed-academic-year denial to the UI.
 *
 * Broadcasts a DOM event rather than raising a toast: a corner toast is easy
 * to miss at the moment someone has just pressed Save, and the write did not
 * happen — that needs acknowledging, not glancing at. `YearLockedDialog`
 * listens for this and shows a centred dialog.
 *
 * Kept free of React and of the toast layer on purpose: this module is
 * imported by ~266 files, so it must stay dependency-light and free of
 * circular imports.
 *
 * De-duplicated on a short window because one bulk action can produce many
 * denials, and the user only needs telling once.
 */
export const YEAR_LOCKED_EVENT = 'nwsisd:year-locked';

let lastYearLockSignalAt = 0;
function notifyYearLocked(rawMessage: string): void {
  const now = Date.now();
  if (now - lastYearLockSignalAt < 4000) return;
  lastYearLockSignalAt = now;

  // Shares the parser with the rest of the year-lock code rather than keeping a
  // second copy of the regex here. `year-lock` is a dependency-free module, so
  // importing it does not weigh this file down. The database names the CLOSED
  // year first, and that helper takes the first match — the distinction matters
  // when a change spans two years.
  const year = yearFromLockError({ message: rawMessage });

  try {
    window.dispatchEvent(new CustomEvent(YEAR_LOCKED_EVENT, { detail: { year } }));
  } catch {
    // No DOM (tests / SSR). The caller still receives the 403 and can handle
    // it; never throw from here.
    console.warn('[year-lock] write refused:', year ?? 'closed year');
  }
}

// Auth lock that uses the Web Locks API when available but NEVER throws on contention.
// Supabase's default `navigatorLock` raises NavigatorLockAcquireTimeoutError when the lock
// is already held (multiple tabs, or a slow token refresh). auth-js then tries to set
// `.isAcquireTimeout` on the (non-extensible) error, producing the unhandled rejection seen
// on /school (Sentry MAGNETFLOW-PORTAL-T). With `{ ifAvailable: true }` the callback always
// runs — whether or not the lock was granted — so refresh proceeds and the timeout path is
// never taken. Trade-off: cross-tab refreshes aren't strictly serialized, which Supabase
// tolerates (this matches the pre-Web-Locks default behavior).
async function safeNavigatorLock<R>(
  name: string,
  _acquireTimeout: number,
  fn: () => Promise<R>,
): Promise<R> {
  if (typeof navigator === 'undefined' || !navigator.locks?.request) {
    return await fn();
  }
  return await navigator.locks.request(name, { ifAvailable: true }, async () => await fn());
}

// Create and export the typed Supabase client with robust connection settings
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    // Single explicit storage key so there is exactly one source of truth for the
    // session and one lock holder per origin.
    storageKey: 'nwsisd-portal-auth',
    lock: safeNavigatorLock
  },
  global: {
    headers: {
      'X-Client-Info': 'nwsisd-portal'
    },
    fetch: async (url, options = {}) => {
      const response = await fetch(url, {
        ...options,
        // Add timeout and retry logic
        signal: AbortSignal.timeout(30000), // 30 second timeout
      });

      // Closed academic year: the DB trigger denies with SQLSTATE PT403, which
      // PostgREST returns as HTTP 403. Without this, every one of the ~438
      // write call sites would surface a raw Postgres error to a school
      // secretary. Handled here because this is the single fetch every
      // PostgREST write, RPC and function invoke passes through.
      // Non-destructive: the response is cloned, so callers still read it.
      if (response.status === 403) {
        try {
          const body = await response.clone().json();
          // The staff-preview guard also answers PT403 (PREVIEW_CODE_*); that is not a year lock.
          const message = String(body?.message ?? '');
          if ((body?.code === 'PT403' && !message.startsWith('PREVIEW_CODE')) || message.includes('ACADEMIC_YEAR_LOCKED')) {
            notifyYearLocked(String(body?.message ?? ''));
          }
        } catch {
          // Not JSON, or already consumed — fall through untouched.
        }
      }

      return response;
    }
  },
  db: {
    schema: 'public'
  },
  realtime: {
    // Disable realtime to reduce connection load
    params: {
      eventsPerSecond: 2
    }
  }
});

// Google OAuth helper functions
export const signInWithGoogle = async (options?: {
  redirectTo?: string;
  invitationToken?: string;
}) => {
  const redirectTo = options?.redirectTo || `${window.location.origin}/auth/callback`;
  const finalRedirectTo = options?.invitationToken
    ? `${redirectTo}?invitation_token=${options.invitationToken}`
    : redirectTo;

  return supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: finalRedirectTo,
      queryParams: options?.invitationToken ? {
        invitation_token: options.invitationToken
      } : undefined
    }
  });
};

export const handleGoogleCallback = async () => {
  const { data, error } = await supabase.auth.getSession();
  return { data, error };
};
